import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';

interface WorkflowDecisionModalSignature {
  Args: {
    isApproving: boolean;
    onClose: () => void;
    onConfirm: (reason?: string) => void | Promise<void>;
  };
}

export default class WorkflowDecisionModal extends Component<WorkflowDecisionModalSignature> {
  @tracked reason = '';

  get actionText(): string {
    return this.args.isApproving ? 'Approve' : 'Deny';
  }

  get title(): string {
    return `${this.actionText} visitor`;
  }

  @action
  handleConfirm(): void {
    const trimmedReason = this.reason.trim();
    void this.args.onConfirm(trimmedReason || undefined);
  }

  @action
  handleClose(): void {
    this.reason = '';
    this.args.onClose();
  }

  @action
  updateReason(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.reason = target.value;
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{false}}
      @translucentOverlay={{true}}
      @onClose={{this.handleClose}}
      @targetAttachment="center"
      @containerClass="ember-modal-dialog !max-w-[500px]"
      @overlayClass="ember-modal-overlay--fadeIn"
    >
      <FocusTrapper>
        <div class="p-8" data-test-workflow-decision-modal>
          <h2 class="text-2xl mb-4" data-test-workflow-decision-modal-title>{{this.title}}</h2>
          <p class="text-carbon-60 mb-6">
            {{if
              @isApproving
              "Please provide an optional reason for approving this visitor."
              "Please provide an optional reason for denying this visitor."
            }}
          </p>
          <div class="mb-6">
            <textarea
              id="workflow-decision-reason"
              data-test-workflow-decision-reason
              class="w-full p-3 border border-carbon-20 rounded resize-none"
              rows="4"
              placeholder="Enter reason..."
              value={{this.reason}}
              {{on "input" this.updateReason}}
            ></textarea>
          </div>
          <ButtonGroup class="flex justify-end" @reverse={{true}}>
            <button
              type="button"
              class="btn btn--small btn--brand"
              data-test-workflow-decision-confirm
              {{on "click" this.handleConfirm}}
            >
              {{this.actionText}}
            </button>
            <button
              type="button"
              class="btn btn--small btn--outline"
              data-test-workflow-decision-cancel
              {{on "click" this.handleClose}}
            >
              Cancel
            </button>
          </ButtonGroup>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
